// Peer positions on the map, through the placement rule (peerPlacement.js): UWB, GPS (server or
// Bluetooth-carried) or Bluetooth presence, glided so a change of source never teleports a friend,
// clamped to the edition's bounds. Shared by the MapLibre map (FestivalMap) and the three.js map (FestivalMap3D).
import { createPlacementTracker } from './peerPlacement';

export const CLAMP_MARGIN_DEG = 0.0025;

/** One tracker per app: the glide state must be the same whoever asks (the 3D tick, the person row, the arrow). */
export const mapPlacementTracker = createPlacementTracker();

/** What the engine's row says about UWB and Bluetooth, in the placement rule's terms. */
export function placementInputsFromRow(row) {
  const peer = row?.peer ?? {};
  const uwb =
    row?.uwbActive && row.uwbDistance != null
      ? {
          active: true,
          distanceM: row.uwbDistance,
          // The UWB bearing only - row.bearingDeg falls back to the GPS bearing, which must never place a UWB range.
          bearingDeg: Number.isFinite(peer.uwbBearing) ? peer.uwbBearing : null,
          directionAvailable:
            row.uwbDirectionAvailable === true && Number.isFinite(peer.uwbBearing),
        }
      : null;
  const ble =
    row?.display?.kind === 'ble-in-range' || Number.isFinite(peer.smoothedRssi)
      ? {
          lastSeenAt: peer.lastSeenAt ?? null,
          estimatedDistanceM:
            Number.isFinite(peer.estimatedDistance) && peer.estimatedDistance > 0
              ? peer.estimatedDistance
              : null,
        }
      : null;
  return { uwb, ble };
}

export function buildPeerPositions({
  peerRows,
  gpsPeers,
  selfPosition,
  bounds,
  tracker = mapPlacementTracker,
  now = Date.now(),
}) {
  const byId = new Map(gpsPeers.map((p) => [p.id, p]));
  const M = CLAMP_MARGIN_DEG;
  const inBounds = (lat, lng) =>
    !bounds ||
    (lng >= bounds[0][0] - M &&
      lng <= bounds[1][0] + M &&
      lat >= bounds[0][1] - M &&
      lat <= bounds[1][1] + M);
  const out = [];
  for (const row of peerRows) {
    const gps = byId.get(row.peerId) ?? null;
    const { uwb, ble } = placementInputsFromRow(row);
    // The radar's verdict rides along, so a friend the radar calls Bluetooth-nearby is not drawn at a stale fix.
    const display = row?.display
      ? { kind: row.display.kind ?? null, distanceMeters: row.display.distanceMeters ?? null }
      : null;
    const placed = tracker.place(
      row.peerId,
      { self: selfPosition ?? null, gps, uwb, ble, display },
      now,
    );
    if (placed.lat == null || placed.lng == null) continue;
    if (!inBounds(placed.lat, placed.lng)) continue;
    out.push({
      peerId: row.peerId,
      displayName: row.displayName,
      photoUrl: row.photoUrl,
      kind: row.display?.kind ?? null,
      lat: placed.lat,
      lng: placed.lng,
      source: placed.source,
      distanceM: placed.distanceM,
      bearingDeg: placed.bearingDeg,
      arrow: placed.arrow,
      arrowMinM: placed.arrowMinM,
      arriveM: placed.arriveM,
      ring: placed.ring,
      glideMs: placed.glideMs,
      accuracyMeters: placed.accuracyMeters,
      lastKnownAt: placed.lastKnownAt,
      fused: placed.fused,
    });
  }
  return out;
}

/**
 * A friend's last fix stops being "where they are" and becomes "where they were". Their phone
 * died, the app closed, they lost signal - they stay on the map where they were last seen, at
 * full brightness, because that spot is still the best guess; tapping them says how old it is
 * (operator, 2026-09-25). The radar's own stale bar (GPS_STALE_THRESHOLD_MS, 60 s) is far too
 * tight for the map: a friend standing still publishes slowly.
 */
export const MAP_LIVE_MS = 5 * 60 * 1000;
export const MAP_STALE_MS = 30 * 60 * 1000;
export const MAP_OLD_MS = 60 * 60 * 1000;
/**
 * Past this a last-seen spot is dropped. Not tied to the festival's hours - sets run past
 * midnight - just long enough that last night's positions are gone by the next afternoon and a
 * dead phone from the afternoon still shows through the evening.
 */
export const MAP_EXPIRE_MS = 6 * 60 * 60 * 1000;

/**
 * How much to trust a last-seen time, as a colour: 'live' (5 min or less, green), 'recent' (to
 * 30 min, plain text), 'stale' (to an hour, gold), 'old' (past an hour, coral) - the app's status
 * colours, .seen--* in index.css. UWB-placed positions are measured this instant.
 */
export function positionAgeTone(p, now = Date.now()) {
  if (p?.fused || !Number.isFinite(p?.lastKnownAt)) return 'live';
  const age = now - p.lastKnownAt;
  if (age <= MAP_LIVE_MS) return 'live';
  if (age <= MAP_STALE_MS) return 'recent';
  if (age <= MAP_OLD_MS) return 'stale';
  return 'old';
}

export function isPositionExpired(p, now = Date.now()) {
  return !p?.fused && Number.isFinite(p?.lastKnownAt) && now - p.lastKnownAt > MAP_EXPIRE_MS;
}
